import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useState,
} from 'react';
import {
    AnalyticsClient,
    AnalyticsClientOptions,
} from '../../clients/analytics';

interface AnalyticsProviderProps {
    client?: AnalyticsClient; // If set, will use the passed client rather than create a new one - useful for embedding the widget in a larger app
    baseUrl: string;
    options?: Partial<AnalyticsClientOptions>;
}

interface AnalyticsContextType {
    client: AnalyticsClient | null;
    sendAllEventsNow: () => Promise<void>;
}

const AnalyticsContext = createContext<AnalyticsContextType | undefined>(
    undefined
);

export const AnalyticsClientProvider: React.FC<AnalyticsProviderProps> = ({
    client: passedClient,
    baseUrl,
    options,
    children,
}) => {
    const [client, setClient] = useState<AnalyticsClient | null>(
        passedClient ?? null
    );

    useEffect(() => {
        if (passedClient) {
            return; // Don't create a new client if we already have one
        }

        if (!baseUrl) {
            return;
        }

        const analyticsClient = new AnalyticsClient(baseUrl, options);
        setClient(analyticsClient);

        return () => {
            analyticsClient.destroy();
            setClient(null);
        };
    }, [baseUrl, options, passedClient]);

    const sendAllEventsNow = useCallback(async () => {
        if (client) {
            await client.sendNow();
        }
    }, [client]);

    return (
        <AnalyticsContext.Provider value={{ client, sendAllEventsNow }}>
            {children}
        </AnalyticsContext.Provider>
    );
};

export const useAnalyticsClient = (): AnalyticsContextType => {
    const context = useContext(AnalyticsContext);
    if (context === undefined) {
        throw new Error(
            'useAnalyticsClient must be used within an AnalyticsClientProvider'
        );
    }
    return context;
};
